import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Eye, EyeOff, Loader2, LockKeyhole } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { useAuth } from "../lib/AuthContext";
import { useNoIndex } from "../lib/useNoIndex";
import { EmSwash } from "../components/EmSwash";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MIN = 8;

const Strength = ({ value }) => {
  const score = [value.length >= MIN, value.length >= 12, /[A-Z]/.test(value) && /[a-z]/.test(value), /\d|[^\w\s]/.test(value)].filter(Boolean).length;
  const label = ["", "Too short", "Okay", "Good", "Strong"][score];
  const cls = ["", "bg-scarlet", "bg-tangerine", "bg-yellow-dark", "bg-grass"][score];
  if (!value) return null;
  return (
    <div className="mt-2 flex items-center gap-2" data-testid="reset-strength" data-score={score}>
      <div className="flex flex-1 gap-1">{[1, 2, 3, 4].map((i) => <span key={i} className={`h-1 flex-1 rounded-full ${i <= score ? cls : "bg-border"}`} />)}</div>
      <span className="text-[13px] font-semibold uppercase tracking-[0.16em] text-olive">{label}</span>
    </div>
  );
};

export default function ResetPassword() {
  useNoIndex();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { adoptSession } = useAuth();
  const [state, setState] = useState("checking"); // checking | invalid | ready
  const [hint, setHint] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    axios.get(`${API}/auth/reset-password/validate`, { params: { token } })
      .then((r) => { setHint(r.data.email_hint || ""); setState(r.data.valid ? "ready" : "invalid"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < MIN) { setError(`Use at least ${MIN} characters.`); return; }
    if (pw !== pw2) { setError("Those two don't match yet."); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      adoptSession(res.data);
      toast.success("Password updated — you're signed in.");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? d[0]?.msg : (d || "Couldn't reset the password. Please try again."));
      if (err?.response?.status === 400) setState("invalid");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="reset-page" className="bg-sand">
      <Navbar onStart={() => navigate("/register")} onDonate={() => navigate("/login")} primaryLabel="Create account" secondaryLabel="Sign in" />
      <section className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 pb-24 pt-32 lg:grid-cols-2 lg:gap-24 lg:px-8 lg:pt-44">
        <div>
          <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">Password reset</span>
          <h1 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[0.95] tracking-tight text-ink">
            Choose a <EmSwash>new one.</EmSwash>
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-olive">
            At least {MIN} characters. A phrase you&rsquo;ll actually remember beats a string you won&rsquo;t. Once you save, you&rsquo;re signed in and every other session is signed out.
          </p>
        </div>

        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="rounded-2xl border-2 border-tangerine bg-tangerine/[0.04] p-6 lg:p-8">
          {state === "checking" && (
            <p className="flex items-center gap-2 text-[13px] text-olive" data-testid="reset-checking"><Loader2 className="h-4 w-4 animate-spin" /> Checking your link…</p>
          )}
          {state === "invalid" && (
            <div data-testid="reset-invalid">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-scarlet/10 text-scarlet"><LockKeyhole className="h-6 w-6" strokeWidth={2} /></span>
              <h2 className="mt-4 font-serif text-3xl text-ink">This link has expired.</h2>
              <p className="mt-3 text-base leading-relaxed text-olive">Reset links work once and last 60 minutes. Request a fresh one and you&rsquo;ll be back in under a minute.</p>
              {error && <p role="alert" data-testid="reset-error" className="mt-3 text-[13px] text-scarlet">{error}</p>}
              <Link to="/forgot-password" data-testid="reset-request-new" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-base font-semibold text-sand hover:bg-forest">Request a new link <ArrowRight className="h-4 w-4" /></Link>
            </div>
          )}
          {state === "ready" && (
            <form onSubmit={submit} data-testid="reset-form">
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-tangerine">For students</span>
              <h2 className="mt-3 font-serif text-3xl text-ink">New password{hint ? <span className="block text-base font-sans not-italic text-olive">for {hint}</span> : "."}</h2>
              <label className="mt-6 block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">New password</span>
                <div className="relative mt-2">
                  <input
                    type={show ? "text" : "password"}
                    required
                    autoFocus
                    minLength={MIN}
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    data-testid="reset-password-input"
                    autoComplete="new-password"
                    className="w-full rounded-md border border-border bg-sand px-4 py-3 pr-12 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
                  />
                  <button type="button" onClick={() => setShow((v) => !v)} aria-label={show ? "Hide password" : "Show password"} data-testid="reset-toggle-show" className="absolute inset-y-0 right-3 text-olive hover:text-ink">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Strength value={pw} />
              </label>
              <label className="mt-4 block">
                <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">Confirm</span>
                <input
                  type={show ? "text" : "password"}
                  required
                  value={pw2}
                  onChange={(e) => setPw2(e.target.value)}
                  data-testid="reset-confirm-input"
                  autoComplete="new-password"
                  className="mt-2 w-full rounded-md border border-border bg-sand px-4 py-3 font-serif text-3xl text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10"
                />
              </label>
              {error && <p role="alert" data-testid="reset-error" className="mt-4 text-[13px] text-scarlet">{error}</p>}
              <button type="submit" disabled={submitting} data-testid="reset-submit" className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-5 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60 sm:w-auto">
                {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</> : <>Save and sign in <ArrowRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </section>
      <Footer variant="minimal" />
    </main>
  );
}
